import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { Paperclip, Send } from "lucide-react";
import { chipToast } from "@/components/ui/chip-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "./profile-form";

const controlClass =
  "h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)";

export function ReportIssueDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");

  const reset = () => {
    setTitle("");
    setDetails("");
  };

  const handleSubmit = () => {
    // No issue-tracker backend yet (deferred) — presentational confirmation.
    chipToast({ message: t`Issue reported. The product team has been notified.` });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Report an issue</Trans></DialogTitle>
          <DialogDescription>
            <Trans>Tell us what went wrong and we&rsquo;ll look into it.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <Field label={t`Issue title`} htmlFor="issue-title">
            <Input
              id="issue-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t`Example: Profile image is not working…`}
              className={controlClass}
            />
          </Field>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="issue-details"
              className="text-xs font-medium text-(--color-text-primary)"
            >
              <Trans>What happened?</Trans>
            </label>
            <div className="flex flex-col rounded-lg border border-(--color-border) bg-(--color-surface-content) focus-within:border-(--color-ring)">
              <textarea
                id="issue-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder={t`Share steps to reproduce, expected results, and actual results…`}
                className="min-h-40 w-full resize-none bg-transparent px-3 py-2.5 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
              />
              <div className="px-3 pb-2.5">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-sm text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)"
                >
                  <Paperclip className="size-5" />
                  <Trans>Attach files</Trans>
                </button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={details.trim().length === 0}
          >
            <Send />
            <Trans>Submit</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
